import React, { useCallback, useState, useRef, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Button, Tooltip } from 'antd'
import { PickerPanel } from '@rc-component/picker'
import dayjsGenerateConfig from '@rc-component/picker/lib/generate/dayjs'
import zhCN from '@rc-component/picker/lib/locale/zh_CN'
import classnames from 'classnames'
import CONFIG from '../../../constants/config'
import sensors from '../../../utils/sensors'
import { useAppSelector } from '../../../app/hooks'

import RangeYearMonthPicker from './RangeYearMonthPicker'
import VipAlert from './VipAlert'
import vip_team from '../../../images/btn-crown-icon.png'
import { useDatesSummary, usePayment, monthBgImgs } from './hooks'

const today = dayjs()

interface RangePaneProps {
  startDate: Dayjs
  endDate: Dayjs
  onChange: (startDate: Dayjs, endDate: Dayjs) => void
  onCancel: () => void
  disabledDate?: (date: Dayjs) => boolean
  dataStatisticsType?: string
  isAllPhoto?: boolean
}

const RangePane: React.FC<RangePaneProps> = (props) => {
  const { onChange, onCancel, dataStatisticsType, isAllPhoto } = props
  const [startDate, setStartDate] = useState(props.startDate.startOf('day'))
  const [endDate, setEndDate] = useState(props.endDate.startOf('day'))
  const [isFinishSelected, setIsFinishSelected] = useState(true)

  // 两个 PickerPanel 完全独立，无"右=左+1"限制，直接显示开始/结束所在月份
  const getInitialPickerValue = (): [Dayjs, Dayjs] => {
    const startMonth = props.startDate.startOf('month')
    const endMonth = props.endDate.startOf('month')
    if (startMonth.isSame(endMonth, 'month')) {
      // 同月：当月显示[上月, 当月]，非当月显示[该月, 该月+1]
      return endMonth.isSame(dayjs(), 'month')
        ? [endMonth.subtract(1, 'month'), endMonth]
        : [startMonth, startMonth.add(1, 'month')]
    }
    // 不同月：左=开始月，右=结束月（独立控制，无需相邻）
    return [startMonth, endMonth]
  }
  const [pickerValue, setPickerValue] = useState<[Dayjs, Dayjs]>(
    getInitialPickerValue
  )
  const firstPickedDateRef = useRef<Dayjs | null>(null)
  const isVip = useAppSelector((state) => state.group.isVip)
  const daysLimit = useAppSelector(
    (state) => state.main.config?.normal_group_data_limit_days
  )
  const datesSummary = useDatesSummary(startDate, endDate, isFinishSelected)

  const onPay = usePayment()

  const isOutOfLimite = useCallback(
    (date: Dayjs) => {
      //超出有效期
      const currentDay = dayjs(date).startOf('day')
      return (
        Math.ceil(today.diff(currentDay, 'days', true)) >
        (parseInt(daysLimit as string) || CONFIG.nomalGroupDatesLimit)
      )
    },
    [daysLimit]
  )

  const isVipAlertVisible = useCallback(() => {
    return (
      !isVip &&
      !isAllPhoto &&
      (isOutOfLimite(dayjs(pickerValue[0]).startOf('month')) ||
        isOutOfLimite(dayjs(pickerValue[1]).endOf('month')))
    )
  }, [pickerValue, isVip, isAllPhoto, isOutOfLimite])

  //已选日期是否有超出有效期部分
  const isPickedOutLimite = useMemo(() => {
    return isOutOfLimite(startDate) || isOutOfLimite(endDate)
  }, [isOutOfLimite, startDate, endDate])

  //切换年月，两个面板各自独立更新
  const onPickerValueChange = (dates: [Dayjs, Dayjs]) => {
    setPickerValue(dates)
  }

  //日期点击：第一次点=开始，第二次点=结束
  const handleDateSelect = useCallback(
    (date: Dayjs) => {
      const trackDay = () => {
        const clickItem =
          !isVip && !isAllPhoto && isOutOfLimite(date) ? 'goldDay' : 'blackDay'
        sensors.track('web_workgroup_filter_time_page_click', {
          clickItem,
          pagePath: dataStatisticsType || window.location.pathname,
          isVip,
          isAllPhoto
        })
      }

      if (!firstPickedDateRef.current) {
        // 第一次点击：记录锚点，显示单日选中
        firstPickedDateRef.current = date
        setStartDate(date.startOf('day'))
        setEndDate(date.startOf('day'))
        setIsFinishSelected(false)
        trackDay()
      } else {
        // 第二次点击：确定范围
        const first = firstPickedDateRef.current
        const [newStart, newEnd] = date.isBefore(first, 'day')
          ? [date.startOf('day'), first.startOf('day')]
          : [first.startOf('day'), date.startOf('day')]
        setStartDate(newStart)
        setEndDate(newEnd)
        firstPickedDateRef.current = null
        setIsFinishSelected(true)
        trackDay()
      }
    },
    [isVip, isAllPhoto, isOutOfLimite, dataStatisticsType]
  )

  //待选第二项，日历鼠标hover时，范围内的日期高亮
  const onCalendarMouseHover = useCallback((current: Dayjs) => {
    if (firstPickedDateRef.current) {
      if (current.isBefore(firstPickedDateRef.current)) {
        setStartDate(current)
        setEndDate(firstPickedDateRef.current)
      }
      if (current.isAfter(firstPickedDateRef.current)) {
        setStartDate(firstPickedDateRef.current)
        setEndDate(current)
      }
    }
  }, [])

  const onSubmit = useCallback(() => {
    if (isPickedOutLimite && !isVip && !isAllPhoto) {
      sensors.track('web_workgroup_filter_time_page_click', {
        clickItem: 'doneVIP',
        pagePath: dataStatisticsType || window.location.pathname,
        isVip,
        isAllPhoto,
        startTime: startDate.format(CONFIG.timeFormat),
        endTime: endDate.format(CONFIG.timeFormat)
      })
      onPay()
      onCancel()
    } else {
      sensors.track('web_workgroup_filter_time_page_click', {
        clickItem: 'done',
        pagePath: dataStatisticsType || window.location.pathname,
        isVip,
        isAllPhoto,
        startTime: startDate.format(CONFIG.timeFormat),
        endTime: endDate.format(CONFIG.timeFormat)
      })
      onChange(startDate.startOf('day'), endDate.endOf('day'))
    }
  }, [
    startDate,
    endDate,
    onChange,
    isPickedOutLimite,
    isVip,
    isAllPhoto,
    onCancel,
    dataStatisticsType,
    onPay
  ])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cellRender = useCallback(
    (current: any, info: { type: string; originNode: React.ReactNode }) => {
      if (info.type !== 'date') {
        return info.originNode
      }
      const isInRange = current.isBetween(startDate, endDate, 'day')
      const isStartDateCell = current.isSame(startDate, 'day')
      const isEndDateCell = current.isSame(endDate, 'day')
      const isOutOfLimiteDate = isOutOfLimite(current)
      // 检查是否被禁用（由外部传入的disabledDate函数决定）
      const isDisabledByProps =
        props.disabledDate && props.disabledDate(current)

      const dateCell = (
        //有效期内的文字样式、选择范围内的样式、部分超出有效期时，选择范围内的样式
        <div
          className={classnames('ant-calendar-date', {
            //默认【文字】黑色，【背景】透明
            'out-of-limit-days':
              (isOutOfLimiteDate && !isVip && !isAllPhoto) || isDisabledByProps, //超出有效期或被禁用的【文字】样式
            'cus-calendar-in-range-date': isInRange, //选择范围内的日期【背景】样式 淡蓝色
            'cus-calendar-start-date': isStartDateCell, //选择范围的开始日期【背景】样式 深蓝色
            'cus-calendar-end-date': isEndDateCell, //选择范围的结束日期【背景】样式 深蓝色
            'cus-calendar-in-range-date-vip':
              isInRange && isPickedOutLimite && !isVip && !isAllPhoto, //非VIP，选择范围内有超限日期，选择范围内的日期【背景】样式 淡黄色
            'cus-calendar-start-date-vip':
              isStartDateCell && isPickedOutLimite && !isVip && !isAllPhoto, //非VIP，选择范围内有超限日期，开始日期【背景】样式 深黄色
            'cus-calendar-end-date-vip':
              isEndDateCell && isPickedOutLimite && !isVip && !isAllPhoto //非VIP，选择范围内有超限日期，结束日期【背景】样式 深黄色
          })}
          onMouseEnter={() => onCalendarMouseHover(current)}
        >
          {current.date()}
        </div>
      )

      if (isDisabledByProps) {
        return <Tooltip title="不可选择此日期">{dateCell}</Tooltip>
      } else if (isOutOfLimiteDate && !isVip && !isAllPhoto) {
        return <Tooltip title="需开通团队会员">{dateCell}</Tooltip>
      }
      return dateCell
    },
    [
      startDate,
      endDate,
      isVip,
      isAllPhoto,
      isPickedOutLimite,
      isOutOfLimite,
      props.disabledDate,
      onCalendarMouseHover
    ]
  )

  const disabledDate = useCallback(
    (current: Dayjs) => {
      //禁止未来日期
      const isAfterToday = current.isAfter(dayjs(), 'day')

      // 如果有外部传入的disabledDate函数，则同时应用两个条件
      if (props.disabledDate) {
        return isAfterToday || props.disabledDate(current)
      }

      return isAfterToday
    },
    [props.disabledDate]
  )

  // 两个面板共享的 props
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sharedPanelProps: any = {
    prefixCls: 'ant-picker',
    generateConfig: dayjsGenerateConfig,
    locale: zhCN,
    picker: 'date',
    disabledDate,
    cellRender,
    onSelect: (date: Dayjs) => handleDateSelect(date)
  }

  return (
    <div className="range-picker-pane">
      <RangeYearMonthPicker
        pickerValue={pickerValue}
        onPickerValueChange={onPickerValueChange}
      />
      <div id="range-pane-calendar-wrapper" className="calendar-wrapper">
        {/* 两个独立 PickerPanel 并排，各自控制所在月份 */}
        <div
          className="range-picker-dropdown range-picker-dropdown-reset"
          style={{ display: 'flex' }}
        >
          <PickerPanel
            {...sharedPanelProps}
            pickerValue={pickerValue[0]}
            onPickerValueChange={(date: Dayjs) =>
              setPickerValue([dayjs(date).startOf('month'), pickerValue[1]])
            }
          />
          <PickerPanel
            {...sharedPanelProps}
            pickerValue={pickerValue[1]}
            onPickerValueChange={(date: Dayjs) =>
              setPickerValue([pickerValue[0], dayjs(date).startOf('month')])
            }
          />
        </div>
        <div
          className="range-picker-left-background"
          style={{
            backgroundImage: `url(${monthBgImgs[pickerValue[0].month()]})`
          }}
        ></div>
        <div
          className="range-picker-right-background"
          style={{
            backgroundImage: `url(${monthBgImgs[pickerValue[1].month()]})`
          }}
        ></div>
      </div>
      <div className="summary">{datesSummary}</div>
      <VipAlert visible={isVipAlertVisible()} onCancel={onCancel} />
      <div className="modal-footer">
        <Button
          style={{ marginLeft: '48px' }}
          type="default"
          onClick={() => {
            sensors.track('web_workgroup_filter_time_page_click', {
              clickItem: 'cancel',
              pagePath: dataStatisticsType || window.location.pathname,
              isVip,
              isAllPhoto
            })
            onCancel()
          }}
        >
          取消
        </Button>
        <Button
          style={{ marginLeft: '10px' }}
          type="primary"
          onClick={onSubmit}
          disabled={!isFinishSelected}
        >
          确定
          {isPickedOutLimite && !isVip && !isAllPhoto && (
            <img style={{ width: '20px' }} src={vip_team} />
          )}
        </Button>
      </div>
    </div>
  )
}

export default RangePane
